import type { ExcelStyle } from 'ag-grid-community';
import { merge } from 'lodash-es';
import { StringTransform } from '../util/formatters/StringFormatters';
import { PrimitiveType } from './field3.data-model';

export interface IFormatterDataModel<T> {
  config?: T;
  extends?: string;
  id: string; // This can be a primitive (number, boolean, etc...) or a custom type (dealStage, etc...)
  type?: PrimitiveType | 'currency'; // Primitive type, matches typescript type e.g. number, string, stringArray etc...
  excelStyle?: Omit<ExcelStyle, 'id'>;
  displayName?: string;
  description?: string;
  source?: 'system' | 'user'; // Read-only via the API; set by migrations/system code.
}

export interface IBooleanFormatConfig {
  falseValue: string;
  noValue: string;
  trueValue: string;
}

export interface ICustomNumberFormatConfig extends Intl.NumberFormatOptions {
  adjustWithAccountingErrorMargin?: boolean;
  invalidValue?: string; // NaN or Infinity
  negativeValue?: string;
  noValue?: string; // null or undefined
  suffix?: string;
  zeroValue?: string;
}

export interface IStringFormatConfig {
  emptyValue?: string;
  noValue?: string; // null or undefined
  transforms?: Array<keyof typeof StringTransform>;
}

export interface ICustomDateFormatConfig extends Intl.DateTimeFormatOptions {
  invalidValue?: string;
  noValue?: string; // null or undefined or ''
}

export function createFormatterDataModel<T>(
  overrides: Partial<IFormatterDataModel<T>> = {}
): IFormatterDataModel<T> {
  return merge(
    {
      config: {} as T,
      extends: '',
      id: `formatter-${JSON.stringify(overrides)}`,
      type: 'string',
    },
    overrides
  );
}

export function createUSDFormatterDataModel(maxDecimals = 2, minDecimals = maxDecimals) {
  return createFormatterDataModel<ICustomNumberFormatConfig>({
    type: 'number',
    config: {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: maxDecimals,
      minimumFractionDigits: minDecimals,
    },
  });
}

export function createMultiplierFormatterDataModel(maxDecimals = 2, minDecimals = maxDecimals) {
  const formatterConfig: ICustomNumberFormatConfig = {
    suffix: 'x',
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: minDecimals,
  };

  return {
    id: JSON.stringify(formatterConfig),
    type: 'number',
    config: formatterConfig,
  };
}
